import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import usersService from './usersService';
import { DEFAULT_CLIENT_FIELDS, DEFAULT_OBJECT_FIELDS } from '../utils/profileFields';

const SettingsContext = createContext();

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    show_expenses: true,
    show_address: true,
    show_client: true,
    show_cost: true,
    tax_rate: 7,
    sounds_enabled: false,
    animations_enabled: true,
    client_fields: { ...DEFAULT_CLIENT_FIELDS },
    object_fields: { ...DEFAULT_OBJECT_FIELDS }
  });
  const [loading, setLoading] = useState(true);
  const settingsRef = useRef(settings);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    settingsRef.current = settings;
    document.documentElement.dataset.animations = settings.animations_enabled === false ? 'off' : 'on';
  }, [settings]);

  useEffect(() => {
    const playCompletionSound = () => {
      if (!settingsRef.current.sounds_enabled) return;
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const context = new AudioContext();
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(660, context.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(880, context.currentTime + 0.12);
        gain.gain.setValueAtTime(0.06, context.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.2);
        oscillator.connect(gain).connect(context.destination);
        oscillator.start();
        oscillator.stop(context.currentTime + 0.2);
        oscillator.onended = () => context.close();
      } catch (_) {}
    };
    window.addEventListener('crm:task-completed', playCompletionSound);
    return () => window.removeEventListener('crm:task-completed', playCompletionSound);
  }, []);

  const loadSettings = async () => {
    try {
      const response = await usersService.getSettings();
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      const updated = { ...settings, ...newSettings };
      setSettings(updated);
      await usersService.updateSettings(newSettings);
    } catch (error) {
      console.error('Error updating settings:', error);
      loadSettings(); // Откатываем изменения
    }
  };

  return (
    <SettingsContext.Provider value={{ settings, loading, updateSettings, reloadSettings: loadSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};
